import { Notification, twClassMerge, useScrollToAnchor } from '@healthhub/react-ui';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { ErrorScreenWrapper, MaxWidthContainer } from '@shared/components';
import { FAMILY_FRIENDS_SECTION_ANCHOR_KEY } from '@shared/constants/anchorKey';
import { useFamilyHealth } from '@shared/hooks/useFamilyHealth';
import useOffline from '@shared/hooks/useOffline/useOffline';
import type { RelatedPersonProfile } from '@shared/services/caregivers/types/getRelatedPeople.types';

import FamilyHealthProfileListing from './FamilyHealthProfileListing';

const { Text, Title } = Typography;

const FamilyHealth = () => {
  const { t } = useTranslation('home');

  useScrollToAnchor(FAMILY_FRIENDS_SECTION_ANCHOR_KEY);

  const { familyHealth, refetchFamilyProfile, retrieveFamilyProfileError, isCaregiverActionRequired } =
    useFamilyHealth();
  const isOffline = useOffline();

  return (
    <div
      className="flex w-full justify-center bg-functional-smoke p-6 md:px-8 md:py-6 lg:px-18 lg:pb-20 lg:pt-15"
      id={FAMILY_FRIENDS_SECTION_ANCHOR_KEY}
    >
      <MaxWidthContainer className="flex flex-col gap-6 ">
        <div className="flex flex-col gap-2">
          <Title className="m-0 text-[1.625rem] text-primary-blue-grey-4 lg:text-3.5xl" level={2}>
            {t('peopleUnderMyCare')}
          </Title>
          <Text className="text-sm text-primary-blue-grey">{t('viewYourLovedOnes')}</Text>

          {!!isCaregiverActionRequired && (
            <div className="mt-4">
              <Notification
                customTitle={t('caregiverNotificationMessage.title', { ns: 'translation' })}
                isExpanded
                isShowIconExpand={false}
                message={t('caregiverNotificationMessage.description', { ns: 'translation' })}
                variant="alert"
              />
            </div>
          )}
        </div>
        <div
          className={twClassMerge('grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-6', {
            'lg:grid-cols-1': !!retrieveFamilyProfileError || isOffline,
          })}
        >
          <ErrorScreenWrapper
            apiErrorProps={{
              code: retrieveFamilyProfileError?.code,
              errorScreenProps: {
                errorMsg: retrieveFamilyProfileError?.message,
                onRefresh: () => {
                  refetchFamilyProfile();
                },
              },
              message: retrieveFamilyProfileError?.message,
            }}
          >
            {familyHealth.map(
              ({
                data,
                description,
                icon,
                id,
                defaultProfilePicDisplay,
                isClickable,
                isLoading,
                onClickAddNew,
                onClickProfile,
                title,
              }) => (
                <FamilyHealthProfileListing
                  data={data as RelatedPersonProfile[]}
                  defaultProfilePicDisplay={defaultProfilePicDisplay}
                  description={description}
                  icon={icon}
                  id={id}
                  isClickable={isClickable}
                  isLoading={isLoading}
                  key={title}
                  onClickAddNew={onClickAddNew}
                  onClickProfile={onClickProfile}
                  title={title}
                />
              )
            )}
          </ErrorScreenWrapper>
        </div>
      </MaxWidthContainer>
    </div>
  );
};

export default FamilyHealth;
